import * as React from "react"

import { cn } from "../../../lib/utils"

export type DateRangePreset = {
  id: string
  label: string
  /** Muted second line — e.g. the year a seasonal bracket resolved to. */
  secondaryLabel?: string
  range: { from: Date; to: Date }
}

export type DateRangePresetRailProps = {
  presets: DateRangePreset[]
  activePresetId?: string
  onPresetSelect: (id: string) => void
  /**
   * Shown as a status row beneath the listbox when the active selection matches
   * no preset. It is not an option: there is nothing for it to activate, since a
   * calendar click is the only way into the custom state.
   */
  customLabel?: string
  isCustomActive?: boolean
  listAriaLabel?: string
  className?: string
}

function DateRangePresetRail({
  presets,
  activePresetId,
  onPresetSelect,
  customLabel,
  isCustomActive = false,
  listAriaLabel = "Date range presets",
  className,
}: DateRangePresetRailProps) {
  const activeIndex = presets.findIndex((preset) => preset.id === activePresetId)
  const [focusedIndex, setFocusedIndex] = React.useState(() =>
    activeIndex >= 0 ? activeIndex : 0,
  )
  const optionRefs = React.useRef<(HTMLButtonElement | null)[]>([])

  // Focus the active preset once, when the popover mounts. Re-focusing on every
  // activeIndex change would yank focus out of the calendar mid-range-selection,
  // because the first of two clicks flips the selection to custom.
  const hasAutoFocusedRef = React.useRef(false)
  React.useEffect(() => {
    if (hasAutoFocusedRef.current) return
    hasAutoFocusedRef.current = true
    const target = activeIndex >= 0 ? activeIndex : 0
    setFocusedIndex(target)
    optionRefs.current[target]?.focus()
  }, [activeIndex])

  function moveFocus(nextIndex: number) {
    const clamped = Math.max(0, Math.min(presets.length - 1, nextIndex))
    setFocusedIndex(clamped)
    optionRefs.current[clamped]?.focus()
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault()
        moveFocus(focusedIndex + 1)
        break
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault()
        moveFocus(focusedIndex - 1)
        break
      case "Home":
        event.preventDefault()
        moveFocus(0)
        break
      case "End":
        event.preventDefault()
        moveFocus(presets.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col gap-2 border-border p-2",
        "border-b md:w-56 md:border-b-0 md:border-r",
        className,
      )}
    >
      <div
        role="listbox"
        aria-label={listAriaLabel}
        aria-orientation="vertical"
        onKeyDown={handleKeyDown}
        // A Dialog/Sheet scroll lock (react-remove-scroll) preventDefaults wheel
        // events on `document`. React portals listen on `body`, so stopping here
        // runs first and keeps this portalled rail scrollable inside an open
        // dialog — same fix as `CommandList` and `MultiTagInput`.
        onWheel={(event) => event.stopPropagation()}
        className="flex min-h-0 max-md:flex-row max-md:overflow-x-auto md:flex-1 md:flex-col md:gap-0.5 md:overflow-y-auto md:overscroll-contain"
      >
        {presets.map((preset, index) => {
          const isActive = preset.id === activePresetId
          return (
            <button
              key={preset.id}
              ref={(node) => {
                optionRefs.current[index] = node
              }}
              type="button"
              role="option"
              aria-selected={isActive}
              tabIndex={index === focusedIndex ? 0 : -1}
              onClick={() => onPresetSelect(preset.id)}
              onFocus={() => setFocusedIndex(index)}
              className={cn(
                "flex min-h-11 shrink-0 flex-col justify-center rounded-sm px-2.5 py-1.5 text-left text-sm outline-none transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                isActive
                  ? "bg-primary-subtle font-medium text-primary-subtle-foreground"
                  : "text-foreground",
              )}
            >
              <span className="whitespace-normal break-words">{preset.label}</span>
              {preset.secondaryLabel ? (
                <span className="text-xs text-muted-foreground">
                  {preset.secondaryLabel}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
      {customLabel ? (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={cn(
            "shrink-0 rounded-sm px-2.5 py-1.5 text-sm md:mt-auto",
            isCustomActive
              ? "bg-primary-subtle font-medium text-primary-subtle-foreground"
              : "text-muted-foreground",
          )}
        >
          {customLabel}
        </div>
      ) : null}
    </div>
  )
}

export { DateRangePresetRail }
